import { Link, useParams } from "react-router-dom";

import { useGetChapterDetailsQuery } from "../../slices/chapterApiSlice";

const ChapterDetailsScreen = () => {
  const { id } = useParams();

  const { data: chapter, isLoading, error } = useGetChapterDetailsQuery(id);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-100 via-blue-50 to-indigo-100">
        <div className="rounded-xl bg-white px-8 py-6 shadow-lg text-blue-600 text-lg font-semibold">
          Loading chapter...
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-100 via-blue-50 to-indigo-100">
        <div className="rounded-xl bg-red-100 px-8 py-6 text-red-700 shadow-lg">
          {error?.data?.message || error?.error}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-100 via-blue-50 to-indigo-100 py-8 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Back Button */}

        <Link
          to="/chapters"
          className="inline-flex items-center gap-2 mb-6 rounded-xl bg-white px-4 py-2 shadow hover:bg-blue-50 text-blue-600 transition"
        >
          ← Back to Chapters
        </Link>

        <div className="rounded-3xl bg-white/80 backdrop-blur-lg border border-white/30 shadow-2xl overflow-hidden">
          {/* Header */}

          <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-8 py-8 text-white">
            <h1 className="text-4xl font-bold">{chapter.name}</h1>

            <p className="mt-2 text-blue-100">Chapter Details</p>
          </div>

          {/* Content */}

          <div className="p-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Subject */}

              <div className="rounded-2xl bg-slate-50 p-5 border">
                <h3 className="text-sm font-semibold uppercase tracking-wide text-gray-500 mb-2">
                  Subject
                </h3>

                <p className="text-xl font-semibold text-gray-800">
                  {chapter.subject?.name}
                </p>
              </div>

              {/* Category */}

              <div className="rounded-2xl bg-slate-50 p-5 border">
                <h3 className="text-sm font-semibold uppercase tracking-wide text-gray-500 mb-2">
                  Category
                </h3>

                <p className="text-xl font-semibold text-gray-800">
                  {chapter.subject?.category}
                </p>
              </div>

              {/* Display Order */}

              <div className="rounded-2xl bg-slate-50 p-5 border">
                <h3 className="text-sm font-semibold uppercase tracking-wide text-gray-500 mb-2">
                  Display Order
                </h3>

                <p className="text-xl font-semibold text-gray-800">
                  {chapter.order}
                </p>
              </div>

              {/* Status */}

              <div className="rounded-2xl bg-slate-50 p-5 border">
                <h3 className="text-sm font-semibold uppercase tracking-wide text-gray-500 mb-2">
                  Status
                </h3>

                {chapter.isActive ? (
                  <span className="inline-flex rounded-full bg-green-100 px-4 py-2 text-green-700 font-semibold">
                    Active
                  </span>
                ) : (
                  <span className="inline-flex rounded-full bg-red-100 px-4 py-2 text-red-700 font-semibold">
                    Inactive
                  </span>
                )}
              </div>

              {/* Created By */}

              <div className="rounded-2xl bg-slate-50 p-5 border">
                <h3 className="text-sm font-semibold uppercase tracking-wide text-gray-500 mb-2">
                  Created By
                </h3>

                <p className="text-lg font-semibold text-gray-800">
                  {chapter.createdBy?.name}
                </p>

                <p className="text-gray-500 mt-1">{chapter.createdBy?.phone}</p>
              </div>

              {/* Created At */}

              <div className="rounded-2xl bg-slate-50 p-5 border">
                <h3 className="text-sm font-semibold uppercase tracking-wide text-gray-500 mb-2">
                  Created On
                </h3>

                <p className="text-lg font-semibold text-gray-800">
                  {new Date(chapter.createdAt).toLocaleString()}
                </p>
              </div>
            </div>

            {/* Description */}

            <div className="mt-8 rounded-2xl bg-slate-50 border p-6">
              <h3 className="text-lg font-bold text-gray-800 mb-4">
                Description
              </h3>

              <p className="leading-8 text-gray-600 whitespace-pre-wrap">
                {chapter.description
                  ? chapter.description
                  : "No description available."}
              </p>
            </div>

            {/* Footer */}

            <div className="mt-8 flex justify-end">
              <Link
                to={`/chapters/${chapter._id}/edit`}
                className="rounded-xl bg-gradient-to-r from-amber-500 to-orange-500 px-6 py-3 font-semibold text-white shadow-lg hover:from-amber-600 hover:to-orange-600 transition"
              >
                Edit Chapter
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChapterDetailsScreen;
